import { logger } from './Helpers';

/**
 * @typedef { Object } EventObj
 * @property { string } EventObj.eventName
 * @property { Object } EventObj.payload
 */

/**
 * @typedef { Object } ListenerOpts
 * @property { boolean } [ListenerOpts.once]
 */

/**
 * @typedef { Object } Listener
 * @property { ListenerOpts } Listener.opts
 * @property { (evtObj: EventObj) => void } func
 */

/**
 * @typedef { { listeners: Listener[], history: {}[] } } Channel
 */

/**
 * @class EventManager
 * @description Listen to events on the page and manage it accross components.
 */
export default class EventManager {
  constructor(eventListeners) {
    this.eventListeners = eventListeners;
    this.events = {};
    this.attachListeners();

    this.logger = logger;

    /**
     * @type { {[key: string]: Channel } }
     */
    this.channels = {};
  }

  attachListeners() {
    if (this.eventListeners) {
      this.eventListeners.forEach((listener) => {
        listener.target.addEventListener(listener.eventName, () => {
          if (!this.events[listener.eventName]) {
            this.events[this.getNodeName(listener.target) + listener.eventName] = {
              isTriggered: true,
            };
          }
        });
      });
    }
  }

  getNodeName(target) {
    if (window.self === target) {
      return '';
    }

    if (target.nodeName) {
      return target.nodeName.toLowerCase() + '_';
    }

    return '';
  }

  isEventTriggered(eventName) {
    return this.events[eventName];
  }

  /**
   * Add a channel and returns it
   *
   * @param { string } eventName
   * @return { Channel }
   */
  addChannel(eventName) {
    this.channels[eventName] = {
      listeners: [],
      history: [],
    };

    return this.channels[eventName];
  }

  /**
   * @param { string } eventName
   * @return { Channel | undefined }
   */
  getChannel(eventName) {
    return this.channels[eventName];
  }

  /**
   * Check if an event has been dispatched before.
   *
   * @param { string } eventName - Name of the event to check
   * @returns { boolean } True if the event has been dispatched before, false otherwise.
   */
  dispatched(eventName) {
    const channel = this.getChannel(eventName);

    if (channel) {
      return channel.history.length > 0;
    }

    return false;
  }

  /**
   * @param { Channel } channel
   * @param { Listener['func'] } listener
   * @param { Object } opts
   */
  addListenerToChannel(channel, listener, opts) {
    channel.listeners.push({
      opts,
      func: listener,
    });
  }

  /**
   * @param { Object } param0
   * @param { string } param0.eventName
   * @param { Channel } param0.channel
   * @param { Listener['func'] } param0.func
   * @param { number } [param0.howMany]
   */
  readHistory({ eventName, channel, func, howMany = 0 }) {
    /**
     * @param { Object } payload
     */
    const safeFunc = (payload) => {
      try {
        func({
          eventName,
          payload,
        });
      } catch (error) {
        this.logger.error(error);
      }
    };

    const cap =
      howMany > 0 ? Math.min(channel.history.length, howMany) : channel.history.length;

    channel.history.slice(0, cap).forEach(safeFunc);
  }

  /**
   * Add event listener
   *
   * @param { string } eventName - Name of the event
   * @param { (obj: {}) => void } listener - Callback function to execute when event is triggered
   * @param { Object } [opts] - Listener opts
   * @param { boolean } [opts.once] - Run only the first dispatch
   *
   * @example
   * addEventListener('click', () => {
   *   console.log('Clicked!');
   * })
   */
  addEventListener(eventName, listener, opts = {}) {
    let channel = this.getChannel(eventName);

    if (channel) {
      const isListenerMissing = !channel.listeners.find(
        (listener_) => listener_.func === listener
      );

      if (isListenerMissing) {
        if (opts.once) {
          // Run only the first in history if available
          if (channel.history.length > 0) {
            this.readHistory({ eventName, channel, func: listener, howMany: 1 });
          } else {
            this.addListenerToChannel(channel, listener, opts);
          }
        } else {
          this.readHistory({ eventName, channel, func: listener });
          // Add listener to existing channel
          this.addListenerToChannel(channel, listener, opts);
        }
      }
    } else {
      // Add channel and retrieve it
      channel = this.addChannel(eventName);
      // Add listener to new channel
      this.addListenerToChannel(channel, listener, opts);
    }
  }

  /**
   * @param { EventObj } eventObj
   * @returns { (listener: Listener) => void }
   */
  execListener(eventObj) {
    return (listener) => {
      if (listener.opts.once) {
        this.removeEventListener(eventObj.eventName, listener.func);
      }

      try {
        listener.func(eventObj);
      } catch (error) {
        this.logger.error(error);
      }
    };
  }

  /**
   * Dispatch event to listeners
   *
   * @param {string} eventName - Name of the event
   * @param {Object} [payload] - Data to pass to the listener callback
   *
   * @example
   * dispatchEvent('dataLoaded', {data: [1, 2, 3]})
   */
  dispatchEvent(eventName, payload = {}) {
    let channel = this.getChannel(eventName);

    const eventObj = {
      eventName,
      payload,
    };

    if (channel) {
      // Run listeners
      channel.listeners.forEach(this.execListener(eventObj));
    } else {
      // Add channel and retrieve
      channel = this.addChannel(eventName);
    }

    // Always track payload
    channel.history.push(payload);
  }

  /**
   * Remove event listener
   *
   * @param { string } eventName - Name of the event
   * @param { Function } listener - Previously added listener
   *
   * @example
   * removeEventListener('click', previouslyAddedListener);
   */
  removeEventListener(eventName, listener) {
    const channel = this.getChannel(eventName);

    if (channel) {
      const index = channel.listeners.findIndex(
        (listener_) => listener_.func === listener
      );

      if (index > -1) {
        channel.listeners = [
          ...channel.listeners.slice(0, index),
          ...channel.listeners.slice(index + 1),
        ];
      }
    }
  }
}
